import { Directive, ElementRef, inject, input, model, signal, OnDestroy, effect } from '@angular/core';

@Directive({
  selector: '[appZoomable]',
  standalone: true,
  exportAs: 'appZoomable',
})
export class ZoomableDirective implements OnDestroy {
  private el = inject(ElementRef);

  enableZoom = input<boolean>(true);
  enablePan = input<boolean>(true);
  enableWheelZoom = input<boolean>(true);
  zoomScale = model<number>(1.0);
  zoomSpeed = input<number>(0.0025);
  minScale = input<number>(1.0);
  maxScale = input<number>(4.0);
  disableTouchMove = input<boolean>(false);

  panX = signal<number>(0);
  panY = signal<number>(0);
  isDragging = signal<boolean>(false);

  private dragStartX = 0;
  private dragStartY = 0;
  private initialPanX = 0;
  private initialPanY = 0;
  private cleanupListeners: (() => void)[] = [];

  constructor() {
    const hostEl = this.el.nativeElement;
    
    effect(() => {
      hostEl.style.touchAction = this.disableTouchMove() ? 'pan-y' : 'none';
    });

    let activeTouchCount = 0;
    let touchStartDist = 0;
    let touchStartScale = 1.0;
    let touchStartPanX = 0;
    let touchStartPanY = 0;
    let touchStartMidX = 0;
    let touchStartMidY = 0;

    const onMouseDown = (event: MouseEvent) => {
      if (!this.enablePan()) return;

      const target = event.target as HTMLElement;
      if (target.closest('.zoom-toolbar') || target.closest('button') || target.closest('input')) {
        return;
      }

      event.preventDefault();
      this.isDragging.set(true);

      this.dragStartX = event.pageX;
      this.dragStartY = event.pageY;
      this.initialPanX = this.panX();
      this.initialPanY = this.panY();

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!this.isDragging()) return;
        const walkX = moveEvent.pageX - this.dragStartX;
        const walkY = moveEvent.pageY - this.dragStartY;
        this.panX.set(this.initialPanX + walkX);
        this.panY.set(this.initialPanY + walkY);
      };

      const onMouseUp = () => {
        this.isDragging.set(false);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onWheel = (event: WheelEvent) => {
      if (!this.enableZoom() || !this.enableWheelZoom()) return;
      event.preventDefault();

      if (event.ctrlKey || event.metaKey) {
        const rect = hostEl.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        const oldScale = this.zoomScale();
        let newScale = oldScale - event.deltaY * this.zoomSpeed();
        newScale = Math.min(Math.max(newScale, this.minScale()), this.maxScale());

        if (newScale !== oldScale) {
          const oldPanX = this.panX();
          const oldPanY = this.panY();

          const newPanX = mouseX - (mouseX - oldPanX) * (newScale / oldScale);
          const newPanY = mouseY - (mouseY - oldPanY) * (newScale / oldScale);

          this.zoomScale.set(newScale);
          this.panX.set(newPanX);
          this.panY.set(newPanY);
        }
      } else {
        // Natural 2-finger panning (scroll swipe)
        this.panX.set(this.panX() - event.deltaX);
        this.panY.set(this.panY() - event.deltaY);
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      if (this.disableTouchMove()) return;
      const target = event.target as HTMLElement;
      if (target.closest('.zoom-toolbar') || target.closest('button') || target.closest('input')) {
        return;
      }

      if (event.touches.length === 1 && this.enablePan()) {
        event.preventDefault();
        this.isDragging.set(true);
        activeTouchCount = 1;
        this.dragStartX = event.touches[0].pageX;
        this.dragStartY = event.touches[0].pageY;
        this.initialPanX = this.panX();
        this.initialPanY = this.panY();
      } else if (event.touches.length === 2 && this.enableZoom()) {
        event.preventDefault();
        this.isDragging.set(false);
        activeTouchCount = 2;
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        touchStartDist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
        const rect = hostEl.getBoundingClientRect();
        touchStartMidX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
        touchStartMidY = (touch1.clientY + touch2.clientY) / 2 - rect.top;
        touchStartScale = this.zoomScale();
        touchStartPanX = this.panX();
        touchStartPanY = this.panY();
      }
    };

    const onTouchMove = (event: TouchEvent) => {
      if (this.disableTouchMove()) return;
      if (event.touches.length === 1 && this.enablePan()) {
        event.preventDefault();
        if (activeTouchCount !== 1) {
          activeTouchCount = 1;
          this.dragStartX = event.touches[0].pageX;
          this.dragStartY = event.touches[0].pageY;
          this.initialPanX = this.panX();
          this.initialPanY = this.panY();
          this.isDragging.set(true);
        }
        const walkX = event.touches[0].pageX - this.dragStartX;
        const walkY = event.touches[0].pageY - this.dragStartY;
        this.panX.set(this.initialPanX + walkX);
        this.panY.set(this.initialPanY + walkY);
      } else if (event.touches.length === 2 && this.enableZoom()) {
        event.preventDefault();
        if (activeTouchCount !== 2) {
          activeTouchCount = 2;
          const touch1 = event.touches[0];
          const touch2 = event.touches[1];
          touchStartDist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
          const rect = hostEl.getBoundingClientRect();
          touchStartMidX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
          touchStartMidY = (touch1.clientY + touch2.clientY) / 2 - rect.top;
          touchStartScale = this.zoomScale();
          touchStartPanX = this.panX();
          touchStartPanY = this.panY();
          this.isDragging.set(false);
        }
        if (touchStartDist > 0) {
          const touch1 = event.touches[0];
          const touch2 = event.touches[1];
          const currentDist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
          const rect = hostEl.getBoundingClientRect();
          const currentMidX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
          const currentMidY = (touch1.clientY + touch2.clientY) / 2 - rect.top;

          const scaleRatio = currentDist / touchStartDist;
          let newScale = touchStartScale * scaleRatio;
          newScale = Math.min(Math.max(newScale, this.minScale()), this.maxScale());

          const ratio = newScale / touchStartScale;
          const newPanX = currentMidX - (touchStartMidX - touchStartPanX) * ratio;
          const newPanY = currentMidY - (touchStartMidY - touchStartPanY) * ratio;

          this.zoomScale.set(newScale);
          this.panX.set(newPanX);
          this.panY.set(newPanY);
        }
      }
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (this.disableTouchMove()) return;
      this.isDragging.set(false);
      activeTouchCount = 0;
    };

    hostEl.addEventListener('mousedown', onMouseDown);
    hostEl.addEventListener('wheel', onWheel, { passive: false });
    hostEl.addEventListener('touchstart', onTouchStart, { passive: false });
    hostEl.addEventListener('touchmove', onTouchMove, { passive: false });
    hostEl.addEventListener('touchend', onTouchEnd);
    hostEl.addEventListener('touchcancel', onTouchEnd);

    this.cleanupListeners.push(() => {
      hostEl.removeEventListener('mousedown', onMouseDown);
      hostEl.removeEventListener('wheel', onWheel);
      hostEl.removeEventListener('touchstart', onTouchStart);
      hostEl.removeEventListener('touchmove', onTouchMove);
      hostEl.removeEventListener('touchend', onTouchEnd);
      hostEl.removeEventListener('touchcancel', onTouchEnd);
    });
  }

  resetZoom() {
    this.zoomScale.set(1.0);
    this.panX.set(0);
    this.panY.set(0);
  }

  ngOnDestroy() {
    this.cleanupListeners.forEach((cleanup) => cleanup());
  }
}
